// web/src/admin/BoardCard.tsx — one client on the pipeline board (portal spec 22 §4d): name + days in this stage, the
// next-action line in its tone (do = accent, wait = muted, bad = red, ok = green), small rows for plan / nameservers /
// payments / uptime / runs (each hidden when it doesn't apply), and red flag chips. The whole card links to the client.
import { Link } from 'react-router-dom';
import type { BoardCard as Card, BoardTone } from '../lib/types';
import { Icon } from '../components/Icon';
import { fmtDate } from './adminUi';

const TONE: Record<BoardTone, { text: string; icon: string }> = {
  do: { text: 'text-run', icon: 'chevron-right' },
  wait: { text: 'text-muted', icon: 'clock' },
  bad: { text: 'text-danger', icon: 'alert' },
  ok: { text: 'text-success', icon: 'check' },
};
const PLAN_STATUS: Record<string, string> = {
  none: 'No plan', trialing: 'trialing', active: 'active', past_due: 'past due', unpaid: 'unpaid', canceled: 'canceled',
  incomplete: 'card needed', incomplete_expired: 'expired', paused: 'paused',
};
export function planLabel(p: Card['plan']): string {
  const status = PLAN_STATUS[p.status] ?? p.status;
  if (p.status === 'none') return status;
  const price = p.interval === 'year' ? '$1,000/yr' : p.interval === 'month' ? '$100/mo' : null;
  return price ? `${price} · ${status}` : status;
}
const UPTIME: Record<NonNullable<Card['uptime']>, { dot: string; label: string }> = {
  up: { dot: 'bg-success', label: 'Up' }, down: { dot: 'bg-danger', label: 'Down' }, unknown: { dot: 'bg-muted', label: 'Not checked yet' }, off: { dot: 'border border-hair', label: 'Off' },
};

export function BoardCard({ c }: { c: Card }) {
  const tone = c.next ? TONE[c.next.tone] : null;
  return (
    <Link to={`/admin/clients/${encodeURIComponent(c.slug)}`}
      className="block min-h-11 min-w-0 rounded-lg border border-hair bg-surface p-3 text-sm transition-colors hover:border-run focus-visible:border-run">
      <div className="flex min-w-0 items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="break-words font-medium text-ink">{c.name}</div>
          <div className="truncate text-xs text-muted">{c.slug}{c.org && c.org !== c.name ? ` · ${c.org}` : ''}</div>
        </div>
        <span className="shrink-0 text-xs text-muted" title="Days in this stage">{c.stageDays}d</span>
      </div>
      {c.next && tone && (
        <p className={`mt-2 flex items-start gap-1.5 break-words font-medium ${tone.text}`}>
          <Icon name={tone.icon} size={15} className="mt-0.5 shrink-0" />{c.next.label}
        </p>
      )}
      <dl className="mt-2 grid gap-1 text-xs">
        <Line k="Plan" v={planLabel(c.plan)} />
        {c.nameservers && <Line k="Nameservers" v={c.nameservers === 'done' ? 'done' : 'not yet'} tone={c.nameservers === 'done' ? 'text-success' : 'text-warn'} />}
        {c.payments && <Line k="Payments" v={c.payments === 'ready' ? 'Stripe ready' : 'Stripe not ready'} tone={c.payments === 'ready' ? 'text-success' : 'text-muted'} />}
        {c.uptime && (
          <div className="flex items-center justify-between gap-2">
            <dt className="text-muted">Uptime</dt>
            <dd className="flex items-center gap-1.5 text-ink"><span className={`inline-block size-2 rounded-full ${UPTIME[c.uptime].dot}`} />{UPTIME[c.uptime].label}</dd>
          </div>
        )}
        {(c.lastRunAt || c.nextRunAt) && (
          <Line k="Runs" v={`${c.lastRunAt ? `${fmtDate(c.lastRunAt)}${c.lastRunResult ? ` · ${c.lastRunResult}` : ''}` : 'none yet'}${c.nextRunAt ? ` → ${fmtDate(c.nextRunAt)}` : ''}`} />
        )}
      </dl>
      {c.flags.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {c.flags.map((f) => <span key={f} className="rounded-full bg-danger/10 px-2 py-0.5 text-[11px] text-danger">{f}</span>)}
        </div>
      )}
    </Link>
  );
}

function Line({ k, v, tone = 'text-ink' }: { k: string; v: string; tone?: string }) {
  return (
    <div className="flex min-w-0 items-start justify-between gap-2">
      <dt className="shrink-0 text-muted">{k}</dt>
      <dd className={`min-w-0 break-words text-right ${tone}`}>{v}</dd>
    </div>
  );
}
